import type { TFunction } from 'i18next';

// Extracts a user-friendly error message from different possible API error shapes.
// Falls back to a generic "something went wrong" message when nothing usable is found.
export const getPayMeErrorMessage = (error: any, t: TFunction): string => {
  const fallbackMessage = t('errors.somethingWentWrong');

  if (!error) {
    return fallbackMessage;
  }

  const responseData = error?.response?.data;

  // Format: response.data is a plain string
  // Example: "Some localized error message"
  if (typeof responseData === 'string' && responseData.trim()) {
    return responseData;
  }

  // Format: response.data is an array
  // - Example 1: ["Some localized error message"]
  // - Example 2: ["{'handle_name': ['error message']}"]
  // - Example 3: ["{'detail': 'Payment handle not found.'}"]
  if (Array.isArray(responseData) && responseData.length > 0) {
    const firstItem = responseData[0];

    if (typeof firstItem === 'string' && firstItem.trim()) {
      // Try to handle formats like: "{'handle_name': ['error message']}" or "{'detail': '...'}"
      try {
        // Try to interpret the first string as a JSON-like payload using single quotes
        const jsonString = firstItem.replace(/'/g, '"');
        const parsed = JSON.parse(jsonString);

        if (parsed) {
          // Format: {"detail": "Payment handle not found."}
          if (typeof parsed.detail === 'string' && parsed.detail.trim()) {
            return parsed.detail;
          }

          // Format: {"handle_name": ["error message"]} or {"handle_name": "error message"}
          if (parsed.handle_name) {
            if (Array.isArray(parsed.handle_name) && typeof parsed.handle_name[0] === 'string') {
              const msg = parsed.handle_name[0].trim();
              if (msg) return msg;
            }
            if (typeof parsed.handle_name === 'string' && parsed.handle_name.trim()) {
              return parsed.handle_name;
            }
          }

          // Generic object format: use first key's string or first element of string array
          // Example: {"some_field": ["error message"]} or {"some_field": "error message"}
          const keys = Object.keys(parsed);
          if (keys.length > 0) {
            const value = (parsed as Record<string, any>)[keys[0]];
            if (typeof value === 'string' && value.trim()) {
              return value;
            }
            if (Array.isArray(value) && typeof value[0] === 'string' && value[0].trim()) {
              return value[0];
            }
          }
        }
      } catch {
        // Fallback: use the raw string
        return firstItem;
      }

      // If parsing didn't return anything useful, still use the raw string
      return firstItem;
    }
  }

  // Format: Django-style non_field_errors on the root object
  // Example: {"non_field_errors": ["Some error message"]}
  const nonFieldError =
    Array.isArray(responseData?.non_field_errors) && typeof responseData.non_field_errors[0] === 'string'
      ? responseData.non_field_errors[0]
      : undefined;

  // Format: message as array on root object
  // Example: {"message": ["Some error message"]}
  const messageFromArray =
    Array.isArray(responseData?.message) && typeof responseData.message[0] === 'string'
      ? responseData.message[0]
      : undefined;

  // Common message/detail shapes on the root object
  // Examples:
  // - {"message": "Some error message"}
  // - {"message": ["Some error message"]}
  // - {"detail": "Some error message"}
  // - plus fallbacks to top-level error/message fields
  const directMessage =
    messageFromArray ||
    responseData?.message ||
    responseData?.detail ||
    nonFieldError ||
    error?.message ||
    error?.error ||
    error?.msg;

  if (typeof directMessage === 'string' && directMessage.trim()) {
    return directMessage;
  }

  // Format: response.data.errors is an array
  // - Example 1: {"errors": ["Some error message"]}
  // - Example 2: {"errors": [{"message": "Some error message"}]}
  if (Array.isArray(responseData?.errors) && responseData.errors.length > 0) {
    const firstError = responseData.errors[0];

    if (typeof firstError === 'string' && firstError.trim()) {
      return firstError;
    }

    if (
      firstError &&
      typeof firstError === 'object' &&
      typeof firstError.message === 'string' &&
      firstError.message.trim()
    ) {
      return firstError.message;
    }
  }

  // Format: response.data.errors is an object
  // - Example 1: {"errors": {"field": ["Some error message"]}}
  // - Example 2: {"errors": {"field": "Some error message"}}
  if (
    responseData?.errors &&
    typeof responseData.errors === 'object' &&
    !Array.isArray(responseData.errors)
  ) {
    const firstKey = Object.keys(responseData.errors)[0];
    const value = (responseData.errors as Record<string, any>)[firstKey];

    if (Array.isArray(value) && value.length > 0) {
      const firstValue = value[0];
      if (typeof firstValue === 'string' && firstValue.trim()) {
        return firstValue;
      }
    }

    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }

  // Format: root-level field errors
  // Example: {"is_active": ["Branch is active"]}
  if (responseData && typeof responseData === 'object' && !Array.isArray(responseData)) {
    const reservedKeys = new Set([
      'message',
      'detail',
      'errors',
      'error',
      'msg',
      'non_field_errors',
    ]);
    const fieldKeys = Object.keys(responseData).filter((k) => !reservedKeys.has(k));
    if (fieldKeys.length > 0) {
      const firstKey = fieldKeys[0];
      const value = (responseData as Record<string, unknown>)[firstKey];
      if (Array.isArray(value) && value.length > 0) {
        const first = value[0];
        if (typeof first === 'string' && first.trim()) {
          return first;
        }
      }
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }
  }

  // Fallback formats nested under "error"/"msg"
  // Examples:
  // - {"error": {"message": "Some error message"}}
  // - {"error": {"detail": "Some error message"}}
  // - {"error": "Some error message"}
  // - {"msg": "Some error message"}
  const nestedDetail =
    responseData?.error?.message ||
    responseData?.error?.detail ||
    responseData?.error ||
    responseData?.msg;

  if (typeof nestedDetail === 'string' && nestedDetail.trim()) {
    return nestedDetail;
  }

  return fallbackMessage;
};

